import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  FileText,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  formatCents,
  formatDate,
  formatReference,
  parseEuroToCents,
  todayIso,
  type Invoice,
  type InvoiceLine,
} from '@vuokramani/shared';
import { pageStyles as ps } from '../../components/page.tsx';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  Notice,
  SelectField,
  TextArea,
  TextField,
} from '../../components/ui.tsx';
import { ApiError, errorKey } from '../../lib/api.ts';
import {
  addMonths,
  invoicePdfUrl,
  monthLabel,
  useAddPayment,
  useDeletePayment,
  useRejectReport,
  useReportPaid,
  useUpdateInvoice,
} from '../../lib/invoices.ts';
import { fileUrl } from '../../lib/tenancy.ts';
import s from './invoices.module.css';

export function YearSwitch({ year, onChange }: { year: number; onChange: (y: number) => void }) {
  const { t } = useTranslation();
  return (
    <span className={s.yearSwitch}>
      <Button
        variant="ghost"
        icon
        aria-label={t('invoices.prevYear')}
        onClick={() => onChange(year - 1)}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </Button>
      {year}
      <Button
        variant="ghost"
        icon
        aria-label={t('invoices.nextYear')}
        disabled={year >= new Date().getFullYear() + 1}
        onClick={() => onChange(year + 1)}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </Button>
    </span>
  );
}

/** Laskun avauslinkki: Vuokrantin PDF tai vuokranantajan oma PDF. */
function invoiceLink(i: Invoice): string | null {
  if (i.format === 'app') return invoicePdfUrl(i.id);
  return i.fileId ? fileUrl(i.fileId) : null;
}

/**
 * Kuukausilaskut. Maksetut kuukaudet näkyvät tiiviinä rivinä, jonka saa auki; avoimet ja
 * seuraava erääntyvä ovat aina auki. Vuokranantaja (manage) kirjaa maksut ja muokkaa
 * yksittäisiä laskuja; vuokralainen näkee tilan ja avaa laskun.
 */
export function InvoiceList({
  invoices,
  propertyId,
  manage = false,
  canReport = true,
}: {
  invoices: Invoice[];
  propertyId: string;
  manage?: boolean;
  /** Vuokralaisen "Maksoin" (asunnon asetus). */
  canReport?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [undo, setUndo] = useState<string | null>(null);
  const [reporting, setReporting] = useState<Invoice | null>(null);
  const [confirming, setConfirming] = useState<Invoice | null>(null);
  const [rejecting, setRejecting] = useState<Invoice | null>(null);
  const report = useReportPaid(propertyId);
  const reject = useRejectReport(propertyId);
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const removePayment = useDeletePayment(propertyId);
  const money = (c: number) => formatCents(c, i18n.language);
  const date = (d: string) => formatDate(d, i18n.language);
  const toggle = (id: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <ul className={s.list}>
        {invoices.map((i) => {
          const link = invoiceLink(i);
          const collapsible = i.status === 'paid';
          const open = !collapsible || opened.has(i.id);
          const lastPaid = i.payments.at(-1)?.paidOn;

          if (!open) {
            return (
              <li key={i.id} className={s.compactRow}>
                <button
                  type="button"
                  className={s.compact}
                  aria-expanded={false}
                  onClick={() => toggle(i.id)}
                >
                  <span className={s.month}>{monthLabel(i.period, i18n.language)}</span>
                  <span className={s.meta}>
                    {lastPaid ? t('invoices.paidOnShort', { date: date(lastPaid) }) : ''}
                  </span>
                  <span className={`${s.badge} ${s.paid}`}>{t('invoices.status.paid')}</span>
                  <span className={s.amount}>{money(i.totalCents)}</span>
                  <ChevronDown size={16} aria-hidden="true" className={s.chevron} />
                </button>
              </li>
            );
          }

          return (
            <li key={i.id} className={s.row}>
              <div className={s.main}>
                <span className={s.month}>{monthLabel(i.period, i18n.language)}</span>
                <span className={s.meta}>
                  {t('invoices.dueOn', { date: date(i.dueDate) })}
                  {i.format === 'app' &&
                    ` · ${t('invoices.ref', { ref: formatReference(i.reference) })}`}
                  {i.edited && ` · ${t('invoices.edited')}`}
                </span>
                {i.note && <span className={s.meta}>{i.note}</span>}
              </div>
              <div className={s.side}>
                <span className={`${s.badge} ${s[i.status] ?? ''}`}>
                  {t(`invoices.status.${i.status}`)}
                </span>
                <span className={s.amount}>{money(i.totalCents)}</span>
                {collapsible && (
                  <Button
                    variant="ghost"
                    icon
                    aria-expanded
                    aria-label={t('invoices.collapse')}
                    onClick={() => toggle(i.id)}
                  >
                    <ChevronUp size={16} aria-hidden="true" />
                  </Button>
                )}
              </div>
              <ul className={s.lineList}>
                {i.lines.map((l, n) => (
                  <li key={n}>
                    <span>{l.description}</span>
                    <span>{money(l.amountCents)}</span>
                  </li>
                ))}
              </ul>
              {i.payments.length > 0 && (
                <ul className={s.payments}>
                  {i.payments.map((p) => (
                    <li key={p.id}>
                      <span>
                        {t('invoices.paidRow', {
                          date: date(p.paidOn),
                          amount: money(p.amountCents),
                        })}
                      </span>
                      {manage && (
                        <button
                          type="button"
                          className={`${s.linkButton} ${s.linkDanger}`}
                          onClick={() => setUndo(p.id)}
                        >
                          {t('invoices.undo')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {i.reportedPaidOn && (
                <p className={s.reportNote}>
                  {manage
                    ? t('invoices.reportedBy', {
                        name: i.reportedByName ?? t('invoices.tenant'),
                        date: date(i.reportedPaidOn),
                      })
                    : t('invoices.reportedByMe', { date: date(i.reportedPaidOn) })}
                </p>
              )}
              <div className={s.actions}>
                {manage && i.reportedPaidOn && (
                  <>
                    <button type="button" className={s.linkButton} onClick={() => setConfirming(i)}>
                      {t('invoices.confirm')}
                    </button>
                    <button
                      type="button"
                      className={`${s.linkButton} ${s.linkDanger}`}
                      onClick={() => setRejecting(i)}
                    >
                      {t('invoices.notArrived')}
                    </button>
                  </>
                )}
                {!manage && canReport && i.status !== 'paid' && !i.reportedPaidOn && (
                  <button type="button" className={s.payButton} onClick={() => setReporting(i)}>
                    {t('invoices.iPaid')}
                  </button>
                )}
                {!manage && canReport && i.reportedPaidOn && (
                  <button
                    type="button"
                    className={`${s.linkButton} ${s.linkDanger}`}
                    disabled={report.isPending}
                    onClick={() => report.mutate({ id: i.id, body: { paidOn: null } })}
                  >
                    {t('invoices.cancelReport')}
                  </button>
                )}
                {manage && i.status !== 'paid' && !i.reportedPaidOn && (
                  <button type="button" className={s.linkButton} onClick={() => setPaying(i)}>
                    {t('invoices.markPaid')}
                  </button>
                )}
                {link && (
                  <a className={s.linkButton} href={link} target="_blank" rel="noreferrer">
                    <FileText size={15} aria-hidden="true" />
                    {t('invoices.open')}
                  </a>
                )}
                {manage && (
                  <button type="button" className={s.linkButton} onClick={() => setEditing(i)}>
                    {t('invoices.edit')}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {paying && (
        <PayDialog invoice={paying} propertyId={propertyId} onClose={() => setPaying(null)} />
      )}
      {confirming && (
        <PayDialog
          invoice={confirming}
          propertyId={propertyId}
          initialDate={confirming.reportedPaidOn ?? undefined}
          onClose={() => setConfirming(null)}
        />
      )}
      {reporting && (
        <ReportDialog
          invoice={reporting}
          propertyId={propertyId}
          onClose={() => setReporting(null)}
        />
      )}
      <Dialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title={t('invoices.rejectTitle')}
      >
        <p>{t('invoices.rejectText')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setRejecting(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={reject.isPending}
            onClick={() => reject.mutate(rejecting!.id, { onSuccess: () => setRejecting(null) })}
          >
            {t('invoices.notArrived')}
          </Button>
        </DialogActions>
      </Dialog>
      {editing && (
        <EditInvoiceDialog
          invoice={editing}
          propertyId={propertyId}
          onClose={() => setEditing(null)}
        />
      )}
      <Dialog open={undo !== null} onClose={() => setUndo(null)} title={t('invoices.undoTitle')}>
        <p>{t('invoices.undoText')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setUndo(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={removePayment.isPending}
            onClick={() => removePayment.mutate(undo!, { onSuccess: () => setUndo(null) })}
          >
            {t('invoices.undo')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const euros = (cents: number, lang: string) =>
  (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2 });

/**
 * Maksun kirjaus. Ennakkomaksussa maksu kattaa myös tulevat kuukaudet: summaksi tulee
 * kuukausien yhteissumma, ja jokainen kuukausi merkitään maksetuksi samalla päivällä.
 */
function PayDialog({
  invoice,
  propertyId,
  initialDate,
  onClose,
}: {
  invoice: Invoice;
  propertyId: string;
  /** Vahvistus: vuokralaisen ilmoittama maksupäivä. */
  initialDate?: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const add = useAddPayment(propertyId);
  const remaining = invoice.totalCents - invoice.paidCents;
  const [date, setDate] = useState(initialDate ?? todayIso());
  const [amount, setAmount] = useState(euros(remaining, i18n.language));
  const [prepay, setPrepay] = useState(false);
  const futureMonths = Array.from({ length: 23 }, (_, n) => addMonths(invoice.period, n + 1));
  const [through, setThrough] = useState(futureMonths[10]!);
  const [notice, setNotice] = useState<string>();
  const months = prepay ? futureMonths.indexOf(through) + 2 : 1;
  const cents = parseEuroToCents(amount);
  const invalid = cents === null || cents < 1 || (!prepay && cents > remaining);

  function choosePrepay(on: boolean, month = through) {
    setPrepay(on);
    setThrough(month);
    setNotice(undefined);
    // Arvio: tulevat kuukaudet saman suuruisina. Palvelin tarkistaa ja kertoo oikean summan.
    const count = on ? futureMonths.indexOf(month) + 2 : 1;
    setAmount(euros(remaining + (count - 1) * invoice.totalCents, i18n.language));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (invalid || !date) return;
    setNotice(undefined);
    add.mutate(
      {
        id: invoice.id,
        body: { paidOn: date, amountCents: cents!, throughMonth: prepay ? through : null },
      },
      {
        onSuccess: onClose,
        onError: (e) => {
          // Tulevien kuukausien summat poikkeavat arviosta: näytetään oikea summa.
          if (e instanceof ApiError && e.code === 'amount_mismatch' && e.message) {
            setAmount(euros(Number(e.message), i18n.language));
            setNotice(
              t('invoices.amountUpdated', {
                amount: formatCents(Number(e.message), i18n.language),
              }),
            );
          }
        },
      },
    );
  }

  const error =
    add.error instanceof ApiError && add.error.code === 'amount_mismatch'
      ? null
      : add.error instanceof ApiError && add.error.code === 'no_template'
        ? t('invoices.noTemplateForMonths')
        : add.isError
          ? t(errorKey(add.error))
          : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('invoices.payTitle', { month: monthLabel(invoice.period, i18n.language) })}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <label className={s.check}>
          <input
            type="checkbox"
            checked={prepay}
            onChange={(e) => choosePrepay(e.target.checked)}
          />
          <span>{t('invoices.prepay')}</span>
        </label>
        {prepay && (
          <SelectField
            label={t('invoices.prepayThrough')}
            value={through}
            onChange={(e) => choosePrepay(true, e.target.value)}
          >
            {futureMonths.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m, i18n.language)}
              </option>
            ))}
          </SelectField>
        )}
        <div className={ps.formRow}>
          <TextField
            label={t('invoices.paidOn')}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <TextField
            label={t('invoices.amount')}
            hint={
              prepay
                ? t('invoices.prepayHint', { count: months })
                : t('invoices.remaining', { amount: formatCents(remaining, i18n.language) })
            }
            inputMode="decimal"
            value={amount}
            readOnly={prepay}
            onChange={(e) => setAmount(e.target.value)}
            error={invalid ? t('invoices.amountError') : undefined}
          />
        </div>
        {notice && <Notice>{notice}</Notice>}
        <p className={s.meta}>{t('invoices.payHint')}</p>
        {error && <Alert>{error}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={add.isPending || invalid || !date}>
            {prepay ? t('invoices.prepaySave', { count: months }) : t('invoices.markPaid')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Vuokralaisen "Maksoin": maksupäivä, oletuksena tänään. */
function ReportDialog({
  invoice,
  propertyId,
  onClose,
}: {
  invoice: Invoice;
  propertyId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const report = useReportPaid(propertyId);
  const [date, setDate] = useState(todayIso());

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!date) return;
    report.mutate({ id: invoice.id, body: { paidOn: date } }, { onSuccess: onClose });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('invoices.reportTitle', { month: monthLabel(invoice.period, i18n.language) })}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <p className={s.meta}>
          {t('invoices.reportText', {
            amount: formatCents(invoice.totalCents - invoice.paidCents, i18n.language),
          })}
        </p>
        <TextField
          label={t('invoices.paidOn')}
          type="date"
          value={date}
          max={todayIso()}
          onChange={(e) => setDate(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        {report.isError && <Alert>{t(errorKey(report.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={report.isPending || !date}>
            {t('invoices.iPaid')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function EditInvoiceDialog({
  invoice,
  propertyId,
  onClose,
}: {
  invoice: Invoice;
  propertyId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const update = useUpdateInvoice(propertyId);
  const [lines, setLines] = useState(toDraft(invoice.lines, i18n.language));
  const [dueDate, setDueDate] = useState(invoice.dueDate);
  const [note, setNote] = useState(invoice.note ?? '');
  const [tried, setTried] = useState(false);
  const parsed = parseLines(lines);

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!parsed || !dueDate) return;
    update.mutate(
      { id: invoice.id, body: { lines: parsed, dueDate, note } },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('invoices.editTitle', { month: monthLabel(invoice.period, i18n.language) })}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <p className={s.meta}>{t('invoices.editHint')}</p>
        <LinesEditor lines={lines} onChange={setLines} showErrors={tried} />
        <TextField
          label={t('invoices.dueDate')}
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        <TextArea
          label={t('invoices.note')}
          hint={t('invoices.noteHint')}
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {update.isError && <Alert>{t(errorKey(update.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Laskurivit
// ---------------------------------------------------------------------------

export interface DraftLine {
  key: number;
  description: string;
  amount: string;
}

let nextKey = 1;

export function toDraft(lines: InvoiceLine[], lang: string): DraftLine[] {
  return lines.map((l) => ({
    key: nextKey++,
    description: l.description,
    amount: (l.amountCents / 100).toLocaleString(lang, { minimumFractionDigits: 2 }),
  }));
}

/** Rivit lähetettäviksi, tai null jos jokin rivi on puutteellinen. */
export function parseLines(lines: DraftLine[]): InvoiceLine[] | null {
  const out: InvoiceLine[] = [];
  for (const l of lines) {
    const cents = parseEuroToCents(l.amount);
    if (!l.description.trim() || cents === null || cents < 0) return null;
    out.push({ description: l.description.trim(), amountCents: cents });
  }
  return out.length > 0 ? out : null;
}

export function LinesEditor({
  lines,
  onChange,
  showErrors,
}: {
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
  showErrors: boolean;
}) {
  const { t, i18n } = useTranslation();
  const set = (key: number, patch: Partial<DraftLine>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const total = lines.reduce((n, l) => n + (parseEuroToCents(l.amount) ?? 0), 0);

  return (
    <div className={s.lines}>
      {lines.map((l, index) => {
        const cents = parseEuroToCents(l.amount);
        return (
          <div key={l.key} className={s.lineRow}>
            <TextField
              label={index === 0 ? t('invoices.lineDescription') : ''}
              aria-label={t('invoices.lineDescription')}
              value={l.description}
              onChange={(e) => set(l.key, { description: e.target.value })}
              error={showErrors && !l.description.trim() ? t('errors.required') : undefined}
            />
            <TextField
              label={index === 0 ? t('invoices.lineAmount') : ''}
              aria-label={t('invoices.lineAmount')}
              inputMode="decimal"
              value={l.amount}
              onChange={(e) => set(l.key, { amount: e.target.value })}
              error={showErrors && (cents === null || cents < 0) ? t('errors.amount') : undefined}
            />
            <Button
              variant="ghost"
              icon
              aria-label={t('common.delete')}
              disabled={lines.length === 1}
              onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
              style={index === 0 ? undefined : { marginTop: 0 }}
            >
              <Trash2 size={15} aria-hidden="true" />
            </Button>
          </div>
        );
      })}
      <div>
        <button
          type="button"
          className={s.linkButton}
          onClick={() => onChange([...lines, { key: nextKey++, description: '', amount: '' }])}
        >
          <Plus size={15} aria-hidden="true" />
          {t('invoices.addLine')}
        </button>
      </div>
      <div className={s.lineTotal}>
        <span>{t('invoices.total')}</span>
        <span>{formatCents(total, i18n.language)}</span>
      </div>
    </div>
  );
}
